import React from 'react';
import {
  InnerPageHeader, Card, SegmentedControl, ErrorState, EmptyState,
  Button, Badge, ConfirmDialog, Alert,
  useToast,
} from '@parsina/framework';
import { CalendarX, CheckCircle2, Video } from 'lucide-react';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useDentalChart } from '../../hooks/useDentalChart.js';
import {
  useVisit, useVisitNote, usePriorVisits, useTreatmentPlans, useVisitMedications,
  useVisitOrders, useVisitDiagnoses, useClinicalAssistant, useVisitFiles, useSoapAssistant,
} from '../../hooks/useVisit.js';
import {
  VISIT_TABS, planTeethIndex, isVisitEditable,
  DEFAULT_FOLLOW_UP_INTERVAL, followUpInputFor, outstandingForFollowUp, followUpDuration,
} from './Visit.shared.js';
import { useVideoVisit } from '../../hooks/useVideoVisit.js';
import VisitCall from './sections/VisitCall.jsx';
import VisitOdontogram from './sections/VisitOdontogram.jsx';
import VisitSubjective from './sections/VisitSubjective.jsx';
import VisitTreatmentPlan from './sections/VisitTreatmentPlan.jsx';
import FinishVisitDialog, { finishToast } from './sections/FinishVisitDialog.jsx';
import MedicationsPane from './panes/MedicationsPane.jsx';
import OrdersPane from './panes/OrdersPane.jsx';
import DiagnosisPane from './panes/DiagnosisPane.jsx';
import QuestionsPane from './panes/QuestionsPane.jsx';
import FilesPane from './panes/FilesPane.jsx';
import PatientAvatar from '../../components/PatientAvatar.jsx';
import { VisitSkeletonWeb } from './Visit.skeleton.jsx';
import { unknownMark } from '../../i18n/format';

// جلسهٔ درمان — the desktop visit workspace (dentist-app/docs/dent-visit.md §2).
//
// ┌───────────────────────────────┬──────────────────────────────┐
// │ LEFT — dynamic workspace      │ RIGHT — persistent clinical  │
// │  دارو · دستورات · تشخیص       │  ① odontogram                │
// │  پرسش · فایل‌ها               │  ② subjective (SOAP)         │
// │  ┌─────────────────────────┐  │  ③ اقدام درمانی و پلن درمان  │
// │  │ the active pane         │  │                              │
// │  └─────────────────────────┘  │  vertically stacked, static  │
// └───────────────────────────────┴──────────────────────────────┘
//
// The RIGHT column is first in the DOM and second on screen, and that is
// deliberate on an RTL surface: `direction: rtl` lays grid columns right-to-
// left, so the first child IS the right-hand one. §2 puts the persistent
// clinical view on the right — in the reading-start position for Persian — and
// the dynamic workspace on the left.
//
// EACH COLUMN SCROLLS INDEPENDENTLY. §2's whole claim for the right column is
// that "the clinician always has the patient's dental context in view"; one
// page scroll would carry the odontogram off the top the moment the dentist
// worked down a long orders list.
//
// ── One state, shared by both columns ──────────────────────────────────────
// `selectedTooth` lives HERE rather than inside the odontogram, because it is
// what ties the two halves together: selecting a tooth on the arch fills the
// subjective's tooth chips and pre-fills a plan line, and clicking a plan line
// or a diagnosis's chart evidence selects it back. §3.1.3's "link treatments
// directly to specific teeth on the Odontogram" runs in both directions, and a
// selection owned by one section could only run in one.

const VisitWeb = ({ appointmentId, tab, setTab, goBack }) => {
  const { t, formatNumber, formatDate } = useTranslation();
  const navigate = useAppNavigate();
  const toast = useToast();
  const { visit, loading, error, reload, completing, complete, followUpOutcome } = useVisit(appointmentId);

  // The ids EVERY section scopes on come from the server's answer about this
  // appointment, never from the URL.
  const patientId = visit?.patientId || null;
  const clinicId = visit?.clinicId || null;
  const editable = isVisitEditable(visit);

  // ویزیت تصویری. `probe` only asks whether this deployment offers a call and
  // whether this visit could host one — it mints nothing and starts nothing.
  // The camera is never touched until «تماس تصویری» is pressed.
  const call = useVideoVisit({ patientId, appointmentId, editable });
  React.useEffect(() => { call.probe(); }, [call.probe]);

  const [selectedTooth, setSelectedTooth] = React.useState(null);
  // Each CLICK on the arch, as an event: `{ fdi, seq }` with a counter that
  // rises on every click, including a repeat click on the tooth already
  // selected. «دندان‌های درگیر» toggles on it, which is how a complaint comes
  // to name several teeth without the chart having to hold a selection SET.
  const [toothPick, setToothPick] = React.useState(null);
  const pickTooth = React.useCallback(
    (fdi) => setToothPick((prev) => ({ fdi, seq: (prev?.seq || 0) + 1 })),
    [],
  );
  // A finding ticked on an image in فایل‌ها, on its way to the tooth editor
  // in the odontogram section. Lifted here for the same reason `selectedTooth`
  // is: the two panes that need it are siblings, and neither owns the other.
  const [pendingFinding, setPendingFinding] = React.useState(null);
  // A one-shot request from the chart to the plan section: "open your add form
  // for this tooth". Cleared by the plan once it has acted, so it cannot re-fire
  // on an unrelated re-render.
  const [planToothRequest, setPlanToothRequest] = React.useState(null);

  // «پایان جلسه». The subjective form is the only section holding text that is
  // not already persisted, so the screen keeps a handle on it and flushes it
  // before closing — see finishVisit.
  const subjectivePending = React.useRef(null);
  const [confirmFinish, setConfirmFinish] = React.useState(false);
  // «نوبت بعدی» — held HERE rather than inside the dialog so it survives the
  // re-render while `completeVisit` is in flight, and so `finishVisit` can read
  // what the dentist chose without the dialog handing it back.
  const [followUpInterval, setFollowUpInterval] = React.useState(DEFAULT_FOLLOW_UP_INTERVAL);
  const [finishError, setFinishError] = React.useState(null);

  const chart = useDentalChart({ patientId, clinicId });
  const note = useVisitNote({ patientId, appointmentId });
  const priorVisits = usePriorVisits({ patientId, appointmentId });
  const plans = useTreatmentPlans({ patientId, clinicId, appointmentId });
  const medications = useVisitMedications({ patientId, appointmentId });
  const orders = useVisitOrders({ patientId, appointmentId, clinicId });
  const diagnoses = useVisitDiagnoses({ patientId, appointmentId });
  const assistant = useClinicalAssistant({ patientId, appointmentId });
  const soap = useSoapAssistant({ patientId, appointmentId });
  const files = useVisitFiles({ patientId, appointmentId });

  const planTeeth = React.useMemo(() => planTeethIndex(plans.plans), [plans.plans]);
  const tabs = React.useMemo(() => VISIT_TABS(t), [t]);

  // Until the visit ITSELF resolves nothing else can start, so the whole
  // workspace draws its own shape — header band, the three rail panels, the
  // tab strip and the pane the URL names (DESIGN.md §"Loading & skeleton").
  // Afterwards each region skeletons alone, inside its real Panel.
  if (loading && !visit) {
    return <VisitSkeletonWeb t={t} tab={tab} label={t('common.loading')} />;
  }

  // A failed read and a visit that does not exist are different facts: only one
  // of them is worth a retry button.
  if (error && !visit) {
    return (
      <div
        data-testid="visit-error"
        data-state="error"
        role="alert"
        style={{ height: '100%', background: C.bg, padding: 28 }}
      >
        <Card>
          <ErrorState
            title={t('visit.error.title')}
            description={t('visit.error.body')}
            onRetry={reload}
            retryLabel={t('common.actions.retry')}
          />
        </Card>
      </div>
    );
  }

  if (!visit) {
    return (
      <div data-testid="visit-content" data-state="empty" style={{ height: '100%', background: C.bg, padding: 28 }}>
        <Card>
          <EmptyState
            icon={<CalendarX size={44} color={C.textMuted} />}
            title={t('visit.notFound.title')}
            description={t('visit.notFound.description')}
            action={<Button variant="outline" onClick={goBack}>{t('common.actions.back')}</Button>}
          />
        </Card>
      </div>
    );
  }

  const patient = visit.patient;
  const meta = [
    visit.dateTime ? formatDate(Number(visit.dateTime), 'full') : null,
    patient?.fileNumber ? `${t('common.labels.fileNumber')}${t('common.punct.colon')} ${patient.fileNumber}` : null,
    patient?.age != null ? t('common.units.years', { count: patient.age }) : null,
  ].filter(Boolean);

  /**
   * Save what is unsaved, then close the visit.
   *
   * ORDER MATTERS AND IS NOT REVERSIBLE. `completed` makes every write on this
   * screen refuse, and this app has no way to reopen an appointment — so the
   * flush goes first and a failed flush ABORTS the close. Closing first and
   * saving after would lose the note with no way back in.
   *
   * Only the subjective is flushed because only it holds unsaved state: an
   * order is written by «ثبت دستور», a plan line by its own save, a tooth event
   * as it is charted. There is nothing else pending to collect.
   */
  const finishVisit = async () => {
    setFinishError(null);

    const pending = subjectivePending.current;
    if (pending?.dirty) {
      const saveErr = await pending.submit();
      if (saveErr) {
        setFinishError({ kind: 'subjective' });
        setConfirmFinish(false);
        return;
      }
    }

    // The outstanding lines are what the dialog just showed, so the duration the
    // desk is asked to book is the longest of THOSE — not the doctor's standard
    // slot, which is a consultation and not a restoration.
    const leftUndone = outstandingForFollowUp(plans.plans, plans.records);
    const err = await complete(patientId, followUpInputFor(
      followUpInterval, Date.now(), followUpDuration(leftUndone, plans.catalog),
    ));
    if (err) {
      // Prefer the SERVICE's own sentence over the generic one. Scheduling
      // refuses transitions for reasons the dentist can act on — a cancelled
      // appointment, a desk that moved it — and «بستن جلسه ممکن نشد» hides
      // every one of them behind the same six words.
      setFinishError({ kind: 'complete', message: err?.message || null });
      setConfirmFinish(false);
      return;
    }
    setConfirmFinish(false);
    // The visit is closed and the screen goes read-only, so the ONLY way the
    // dentist learns whether the desk was told is this line. `unavailable` gets
    // its own sentence rather than silence — a prompt that failed to raise is a
    // patient who may never be called back.
    toast.success(finishToast(t, followUpOutcome.current));
  };

  const paneProps = {
    visit, patient, editable, selectedTooth,
    onSelectTooth: setSelectedTooth,
  };

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: C.bg, overflow: 'hidden',
    }}>
      <InnerPageHeader
        onBack={goBack}
        backLabel={t('common.actions.back')}
        title={patient?.name || t('visit.unknownPatient')}
        avatar={<PatientAvatar name={patient?.name || unknownMark()} src={patient?.avatarUrl} size={44} bg={C.navy} />}
        meta={meta}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {visit.status && (
            <Badge variant="outline">{t(`today.status.${visit.status}`)}</Badge>
          )}
          {/* Said out loud, so the screen's missing buttons read as a boundary
              rather than as an omission. */}
          {!editable && <Badge variant="outline">{t('visit.readOnly.badge')}</Badge>}
          {/* Only when the deployment HAS a media server and this visit is
              still open. A button that cannot make a call is worse than no
              button: it reads as broken rather than absent. `available` is null
              until the probe answers, so nothing flickers into view. */}
          {call.available && !call.live && (
            <Button variant="outline" onClick={call.start} disabled={call.busy}>
              <Video size={16} aria-hidden />
              {call.busy ? t('visit.call.connecting') : t('visit.call.start')}
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('patient', { id: patientId })}>
            {t('visit.openRecord')}
          </Button>
          {/* Only while the visit is open. Once closed the read-only badge above
              says so, and a disabled «پایان جلسه» beside it would just repeat it. */}
          {editable && (
            <Button onClick={() => setConfirmFinish(true)} disabled={completing}>
              {/* The kit's Button takes no icon prop — it renders children in a
                  flex row with a gap, so the icon is simply a child. */}
              <CheckCircle2 size={16} aria-hidden />
              {completing ? t('visit.finish.working') : t('visit.finish.action')}
            </Button>
          )}
        </div>
      </InnerPageHeader>

      {/* Says which half failed, because the two need different actions: a
          rejected save leaves the visit OPEN with the text still on screen to
          retry, while a rejected close means the record is saved and only the
          appointment is still open. */}
      {finishError && (
        <div role="alert" style={{ padding: '0 24px 12px' }}>
          <Alert
            variant="error"
            title={t(`visit.finish.error.${finishError.kind}`)}
          >
            {/* The kit's Alert takes the body as CHILDREN. As a `description` prop it
                was spread onto the <div> as an unknown attribute, so the service's
                own sentence — the whole reason this branch prefers it — reached the
                DOM and rendered nothing. */}
            {finishError.message || null}
          </Alert>
        </div>
      )}

      {/* «پایان جلسه», and — only when this visit leaves work planned and not
          performed — «نوبت بعدی». The one moment the dentist is certainly
          thinking about whether the patient must come back is the moment they
          close the visit, so that is where they are asked. */}
      <FinishVisitDialog
        open={confirmFinish}
        onOpenChange={setConfirmFinish}
        onConfirm={finishVisit}
        loading={completing}
        t={t}
        formatNumber={formatNumber}
        outstanding={outstandingForFollowUp(plans.plans, plans.records)}
        interval={followUpInterval}
        onIntervalChange={setFollowUpInterval}
      />

      <div
        data-testid="visit-content"
        data-state="content"
        style={{
          flex: 1, minHeight: 0, display: 'grid',
          // The RIGHT column is wider: it carries the odontogram, which has a
          // fixed aspect ratio and stops being readable when squeezed.
          //
          // The first track is the RIGHT one under dir=rtl, and it holds the
          // first child — the rail. It used to be the 1fr of `1fr 1.05fr`,
          // which gave the extra width to the workspace instead and made the
          // comment above describe the opposite of what shipped.
          gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
          gap: 16, padding: '16px 24px 24px', overflow: 'hidden',
        }}
      >
        {/* ── RIGHT: the persistent clinical view (first child = right in RTL) ── */}
        {/* `visit-rail` sets flex-shrink:0 on the panels (styles.css). Without it
            they compress to fit this box rather than overflow it, so this
            overflowY never has anything to scroll and Panel's overflow:hidden
            crops the difference — the odontogram's lower arch, most of شرح حال. */}
        <div className="visit-rail" style={{
          minHeight: 0, overflowY: 'auto', display: 'flex',
          flexDirection: 'column', gap: 14, paddingInlineEnd: 4,
        }}>
          <VisitOdontogram
            pendingFinding={pendingFinding}
            onFindingConsumed={() => setPendingFinding(null)}
            patientId={patientId}
            clinicId={clinicId}
            t={t}
            formatNumber={formatNumber}
            formatDate={formatDate}
            chart={chart}
            planTeeth={planTeeth}
            records={plans.records || []}
            catalog={plans.catalog || []}
            appointmentId={appointmentId}
            selectedTooth={selectedTooth}
            onSelectTooth={setSelectedTooth}
            onToothPick={pickTooth}
            onPlanTooth={setPlanToothRequest}
            editable={editable}
          />

          <VisitSubjective
            pendingRef={subjectivePending}
            t={t}
            formatNumber={formatNumber}
            formatDate={formatDate}
            note={note}
            editable={editable}
            selectedTooth={selectedTooth}
            toothPick={toothPick}
            priorVisits={priorVisits}
            patient={patient}
            onOpenPriorVisit={(id) => navigate('visit', { appointmentId: id })}
          />

          <VisitTreatmentPlan
            t={t}
            formatNumber={formatNumber}
            plans={plans}
            catalog={plans.catalog}
            clinicId={clinicId}
            appointmentId={appointmentId}
            editable={editable}
            selectedTooth={selectedTooth}
            onSelectTooth={setSelectedTooth}
            addRequestTooth={planToothRequest}
            onAddRequestHandled={() => setPlanToothRequest(null)}
            onChartChanged={chart.reload}
          />
        </div>

        {/* ── LEFT: the dynamic workspace ─────────────────────────────────── */}
        <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flexShrink: 0, marginBottom: 12 }}>
            <SegmentedControl options={tabs} value={tab} onChange={setTab} />
          </div>

          {/* The kit's SegmentedControl declares role="tablist"/role="tab";
              this is the other half of the pattern. `aria-label` rather than
              `aria-labelledby` — the kit generates no ids.
              ON THE WRAPPER, not on <Card>: the kit destructures `role` and
              applies it only to its onClick (button) form, so a role passed to
              a plain Card is silently dropped and the tablist announced tabs
              over a region it had no relationship to. Today.web.jsx carries the
              same note about role="alert". */}
          <div
            role="tabpanel"
            aria-label={tabs.find((s) => s.value === tab)?.label}
            style={{ flex: 1, minHeight: 0, display: 'flex' }}
          >
          <Card style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
            {tab === 'orders' ? (
              <OrdersPane orders={orders} editable={editable} />
            ) : tab === 'diagnosis' ? (
              <DiagnosisPane
                diagnoses={diagnoses}
                note={note}
                files={files.files}
                soap={soap}
                chart={chart}
                {...paneProps}
              />
            ) : tab === 'questions' ? (
              <QuestionsPane
                assistant={assistant}
                diagnoses={diagnoses}
                note={note}
                editable={editable}
              />
            ) : tab === 'files' ? (
              <FilesPane
                files={files}
                visit={visit}
                editable={editable}
                selectedTooth={selectedTooth}
                onConfirmFinding={setPendingFinding}
              />
            ) : (
              <MedicationsPane
                visit={visit}
                patient={patient}
                editable={editable}
                medications={medications}
              />
            )}
          </Card>
          </div>
        </div>
      </div>

      {/* ویزیت تصویری — floats over the workspace rather than replacing it, so
          the odontogram and the subjective stay usable while the patient is on
          screen. Rendered LAST and positioned fixed; it draws nothing until a
          call is starting or live. */}
      <VisitCall call={call} patientName={patient?.name} />
    </div>
  );
};

export default VisitWeb;
